import {useState} from 'react';

import {Button, DatePicker, SearchBar, Select} from '@/components';
import {Filter} from '@/components/Filter';
import {getReferralStatusOptions} from '@/constants/formatters';
import type {ReferralFiltersProps} from '@/types';

const ReferralFilters = ({
  filterCount,
  filtersToApply,
  currentFilters,
  clearFilters,
  selectAppliedFilters,
  handleApplyFilters,
  selectCurrentFilters,
  handleAutoComplete,
}: ReferralFiltersProps) => {
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const fromDate = filtersToApply.fromDate
    ? new Date(filtersToApply.fromDate)
    : undefined;

  const toDate = filtersToApply.toDate
    ? new Date(filtersToApply.toDate)
    : undefined;

  const handleShowFilterChange = (): void => {
    setShowFilters(!showFilters);
  };

  const handleApplyFilter = (): void => {
    // Only close on success. Dismissing the panel after a rejected date range
    // hid the values the user has to correct. See hooks/useFilters.ts.
    if (handleApplyFilters()) {
      setShowFilters(false);
    }
  };

  const handleClearFilters = (): void => {
    setShowFilters(false);
    clearFilters();
  };

  return (
    <>
      <div className="mt-5 w-full">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3 ">
          <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-3/5">
            {/* Search Bar */}
            <SearchBar
              handleSearch={handleAutoComplete}
              handleAutoComplete={handleAutoComplete}
              clearFilters={clearFilters}
              inputClassName="w-full md:w-4/6"
            />

            <div className="flex self-start md:self-center flex-col md:flex-row items-center gap-3">
              {/* Filter Box */}
              <div className="items-center self-start md:self-center gap-3">
                <Filter
                  closeFilter={() => setShowFilters(false)}
                  handleShowFilter={handleShowFilterChange}
                  label="Filters"
                  bodyClassName="w-full sm:w-[450px]"
                  positionLeft={1}
                >
                  {/* Filter Body */}
                  {showFilters && (
                    <div className="p-2 space-y-4 max-w-4xl mx-auto w-full col-span-full">
                      <div className="flex flex-col md:flex-row gap-2">
                        <DatePicker
                          label="From Date"
                          selectedDate={fromDate}
                          setSelectedDate={(date) =>
                            selectAppliedFilters?.('fromDate', date)
                          }
                          placeholderText="Select Date"
                        />

                        <DatePicker
                          label="To Date"
                          selectedDate={toDate}
                          setSelectedDate={(date) =>
                            selectAppliedFilters?.('toDate', date)
                          }
                          placeholderText="Select Date"
                        />
                      </div>

                      <div className="flex justify-end">
                        <Button
                          onClick={handleClearFilters}
                          colorScheme="cyan"
                          variant="ghost"
                        >
                          Clear
                        </Button>

                        <Button
                          onClick={handleApplyFilter}
                          colorScheme="cyan"
                          className="ml-3"
                        >
                          Apply Filters {filterCount && ` (${filterCount})`}
                        </Button>
                      </div>
                    </div>
                  )}
                </Filter>
              </div>
            </div>
          </div>

          {/* Status Select Box */}
          <div className="items-center self-start md:self-center space-x-3">
            <Select
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                selectCurrentFilters('status', e.target.value)
              }
              value={currentFilters.status}
              name="status"
              options={getReferralStatusOptions()}
              placeholder="Filter by Status"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ReferralFilters;
